/**
 * StatPill — card metrica inspirat din mock-ul HosPtal: număr mare bold,
 * delta ca pill colorat (nu simplu text), icon tile solid color cu glow
 * (opțional). Păstrează interfața Props ca să nu rupem callers.
 */
import type { ReactNode } from 'react';

export type StatColor = 'cyan' | 'mint' | 'amber' | 'purple' | 'rose';

// Culoare solid — pentru icon tile background (stil HosPtal)
const SOLID_BG: Record<StatColor, string> = {
  cyan: 'var(--c-cyan)',
  mint: 'var(--c-mint)',
  amber: 'var(--c-amber)',
  purple: 'var(--c-purple)',
  rose: 'var(--c-rose)',
};

// Glow pentru tile (acelaș ton ca SOLID_BG, blur mare, alpha mic)
const SOLID_GLOW: Record<StatColor, string> = {
  cyan: '0 0 18px rgba(34, 200, 224, 0.45)',
  mint: '0 0 18px rgba(19, 233, 180, 0.45)',
  amber: '0 0 18px rgba(255, 167, 38, 0.45)',
  purple: '0 0 18px rgba(184, 116, 255, 0.45)',
  rose: '0 0 18px rgba(255, 76, 122, 0.45)',
};

// Strip accent top de card (gradient liniar orizontal)
const GRADS: Record<StatColor, string> = {
  cyan: 'linear-gradient(90deg, transparent, var(--c-cyan), transparent)',
  mint: 'linear-gradient(90deg, transparent, var(--c-mint), transparent)',
  amber: 'linear-gradient(90deg, transparent, var(--c-amber), transparent)',
  purple: 'linear-gradient(90deg, transparent, var(--c-purple), transparent)',
  rose: 'linear-gradient(90deg, transparent, var(--c-rose), transparent)',
};

interface Props {
  label: string;
  value: string | number;
  color: StatColor;
  icon?: ReactNode;
  /** Delta față de referință: pozitiv (up/mint), negativ (down/rose), zero (neutral/dim). */
  delta?: { value: string; direction: 'up' | 'down' | 'neutral' };
}

export default function StatPill({ label, value, color, icon, delta }: Props) {
  const direction = delta?.direction ?? 'neutral';
  const deltaColor =
    direction === 'up'
      ? 'var(--c-mint)'
      : direction === 'down'
        ? 'var(--c-rose)'
        : 'var(--text-muted)';
  const deltaBg =
    direction === 'up'
      ? 'var(--bg-mint)'
      : direction === 'down'
        ? 'var(--bg-rose)'
        : 'var(--bg-surface)';
  const deltaArrow =
    direction === 'up' ? '↑ ' : direction === 'down' ? '↓ ' : '';

  return (
    <div
      className="relative overflow-hidden rounded-2xl glass-rim"
      style={{
        padding: '20px 22px',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        boxShadow:
          '0 8px 28px rgba(0, 0, 0, 0.18), 0 1px 3px rgba(0, 0, 0, 0.06)',
      }}
    >
      {/* Strip accent subtil sus */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: GRADS[color], opacity: 0.9 }}
        aria-hidden
      />

      {/* Header: label stânga + icon tile dreapta — tile mai mare ca în mock */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div
          className="font-mono uppercase flex-1 min-w-0 truncate"
          style={{
            fontSize: 10,
            letterSpacing: '0.14em',
            color: 'var(--text-muted)',
            fontWeight: 500,
            paddingTop: 4,
          }}
        >
          {label}
        </div>
        {icon && (
          <div
            className="rounded-xl grid place-items-center flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: SOLID_BG[color],
              color: '#ffffff',
              boxShadow: SOLID_GLOW[color],
            }}
            aria-hidden
          >
            {icon}
          </div>
        )}
      </div>

      {/* Număr mare bold — focalul cardului, ~36px ca în mock */}
      <div
        style={{
          fontSize: 36,
          fontWeight: 700,
          letterSpacing: '-0.025em',
          lineHeight: 1.0,
          color: 'var(--text)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>

      {/* Delta ca pill colorat */}
      {delta && (
        <div className="mt-3">
          <span
            className="inline-flex items-center font-mono rounded-md px-2 py-0.5"
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.02em',
              color: deltaColor,
              background: deltaBg,
              border: `1px solid ${direction === 'neutral' ? 'var(--border)' : deltaColor}`,
            }}
          >
            {deltaArrow}
            {delta.value}
          </span>
        </div>
      )}
    </div>
  );
}
